import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { aiAPI, tweetsAPI } from '../api';

const TONE_OPTIONS = [
  { value: 'professional', label: 'プロフェッショナル', desc: '信頼感・専門性' },
  { value: 'casual', label: 'カジュアル', desc: '親しみやすい' },
  { value: 'inspirational', label: 'インスパイア', desc: '前向き・励ます' },
  { value: 'educational', label: '教育的', desc: 'わかりやすく解説' },
  { value: 'promotional', label: 'プロモーション', desc: 'CTA含む告知' },
  { value: 'humorous', label: 'ユーモア', desc: '楽しく面白い' },
];

export default function AiGeneratePage({ onSaved }) {
  const [topic, setTopic] = useState('');
  const [tone, setTone] = useState('professional');
  const [count, setCount] = useState(3);
  const [context, setContext] = useState('');
  const [loading, setLoading] = useState(false);
  const [generated, setGenerated] = useState([]);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});

  const handleGenerate = async () => {
    if (!topic.trim()) return toast.error('トピックを入力してください');
    setLoading(true);
    setGenerated([]);
    try {
      const res = await aiAPI.generate({ topic, tone, count, context });
      setGenerated(res.data.tweets || []);
      toast.success(`${res.data.tweets?.length || 0}件のツイートを生成しました`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'AI生成に失敗しました。APIキーを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (tweet, index) => {
    setSaving(prev => ({ ...prev, [index]: true }));
    try {
      await tweetsAPI.create({ content: tweet.content, status: 'ready', tags: topic });
      setSaved(prev => ({ ...prev, [index]: true }));
      toast.success('ライブラリに保存しました');
    } catch (e) {
      toast.error(e.response?.data?.error || '保存に失敗しました');
    } finally {
      setSaving(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSaveAll = async () => {
    const unsaved = generated.filter((_, i) => !saved[i]);
    if (unsaved.length === 0) return toast.error('すべて保存済みです');

    let count = 0;
    for (let i = 0; i < generated.length; i++) {
      if (!saved[i]) {
        try {
          await tweetsAPI.create({ content: generated[i].content, status: 'ready', tags: topic });
          setSaved(prev => ({ ...prev, [i]: true }));
          count++;
        } catch {}
      }
    }
    toast.success(`${count}件を保存しました`);
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🤖 AI ツイート生成</div>
        <div className="page-sub">Claude AIがツイートを自動生成します</div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 24 }}>
        {/* Left: Input panel */}
        <div>
          <div className="card">
            <div className="form-group">
              <label className="form-label">トピック・テーマ</label>
              <input
                className="input"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                placeholder="例: フリーランスエンジニアの独立方法"
              />
            </div>

            <div className="form-group">
              <label className="form-label">トーン</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {TONE_OPTIONS.map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      cursor: 'pointer',
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: `1px solid ${tone === opt.value ? 'var(--accent)' : 'var(--border)'}`,
                      background: tone === opt.value ? 'var(--accent-dim)' : 'var(--bg-secondary)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="tone"
                      value={opt.value}
                      checked={tone === opt.value}
                      onChange={() => setTone(opt.value)}
                      style={{ display: 'none' }}
                    />
                    <div style={{ fontSize: 13, fontWeight: 500, color: tone === opt.value ? 'var(--accent)' : 'var(--text-primary)' }}>
                      {opt.label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{opt.desc}</div>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">生成数</label>
              <select className="select" value={count} onChange={e => setCount(Number(e.target.value))}>
                <option value={1}>1件</option>
                <option value={3}>3件</option>
                <option value={5}>5件</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">追加コンテキスト（任意）</label>
              <textarea
                className="textarea"
                value={context}
                onChange={e => setContext(e.target.value)}
                placeholder="ターゲット層、含めたいキーワード、除外したい表現など"
                rows={3}
              />
            </div>

            <button
              className="btn btn-primary btn-lg"
              onClick={handleGenerate}
              disabled={loading}
              style={{ width: '100%' }}
            >
              {loading ? '⏳ 生成中...' : '✨ AIで生成する'}
            </button>
          </div>
        </div>

        {/* Right: Generated tweets */}
        <div>
          {generated.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>生成結果 ({generated.length}件)</div>
              <button className="btn btn-secondary btn-sm" onClick={handleSaveAll}>
                📥 すべて保存
              </button>
            </div>
          )}

          {loading && (
            <div style={{ textAlign: 'center', padding: 60 }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🤖</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>AIがツイートを生成中...</div>
            </div>
          )}

          {generated.map((tweet, i) => (
            <div key={i} className="tweet-card" style={{ borderColor: saved[i] ? 'var(--success)' : undefined }}>
              {saved[i] && (
                <div style={{ fontSize: 11, color: 'var(--success)', marginBottom: 6 }}>✅ 保存済み</div>
              )}
              <div className="tweet-card-content">{tweet.content}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 11, color: tweet.content.length > 280 ? 'var(--danger)' : 'var(--text-muted)' }}>
                  {tweet.content.length} / 280文字
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    className="btn btn-sm btn-ghost"
                    onClick={() => navigator.clipboard.writeText(tweet.content).then(() => toast.success('コピーしました'))}
                  >
                    📋 コピー
                  </button>
                  {!saved[i] && (
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleSave(tweet, i)}
                      disabled={saving[i]}
                    >
                      {saving[i] ? '保存中...' : '💾 ライブラリへ保存'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {!loading && generated.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🤖</div>
              <div className="empty-state-text">トピックを入力してAI生成を<br />実行してください</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
